import { ReactElement, cloneElement, useMemo } from 'react';

import { accessorMap } from './renderers';
import { AvailableColumn, ColumnType, DataItemType } from './types';

export const expandedRowClassName = 'expanded-row';
export const customColumnClassName = 'custom-column';
export const withoutTitleClassName = 'without-title';
export const stickyEndClassName = 'sticky-end';

export const ensureSmallIcon = (icon?: ReactElement) => {
  return icon
    ? cloneElement(icon, {
        ...icon.props,
        size: 16,
      })
    : undefined;
};

export const useColumnTypeMap = <T extends DataItemType>(columns: Array<AvailableColumn<T>>) => {
  return useMemo(() => {
    return columns.reduce(
      (columnMap, column) => {
        if (column.type) {
          columnMap[column.id] = column.type;
        }
        return columnMap;
      },
      {} as Record<string, ColumnType>,
    );
  }, [columns]);
};

export const getColumnAccessorFn =
  <T extends DataItemType>(column: AvailableColumn<T>) =>
  (row: T) => {
    const value = row[column.id];
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return undefined;
    }

    if (!column.type) {
      return value;
    }

    const fn = accessorMap[column.type];
    if (fn) {
      return fn(value, column) || value;
    }
    return value;
  };

export const isEmptyColumnValue = (value: unknown) => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value).length === 0)
  );
};
